import * as React from "react";
import { getUsage, Usage } from "../utils/api";
import { useAuth } from "./AuthContext";

export type UsageStatus = "idle" | "loading" | "loaded" | "error";

export type UsageContextValue = {
  usage: Usage | null;
  status: UsageStatus;
  refresh: () => Promise<void>;
};

// A real default (rather than throwing) keeps SSR/prerender of any consumer
// safe: consumers render nothing until the provider hydrates and loads.
const UsageContext = React.createContext<UsageContextValue>({
  usage: null,
  status: "idle",
  refresh: async () => {},
});

export function UsageProvider({ children }: { children: React.ReactNode }) {
  const { status: authStatus } = useAuth();
  const [usage, setUsage] = React.useState<Usage | null>(null);
  const [status, setStatus] = React.useState<UsageStatus>("idle");

  const load = React.useCallback(async (force: boolean) => {
    setStatus("loading");
    const result = await getUsage({ force }).catch(() => null);
    if (result?.data) {
      setUsage(result.data);
      setStatus("loaded");
    } else {
      setUsage(null);
      setStatus("error");
    }
  }, []);

  // refresh() backs the Retry button and post-action reloads, so it always
  // goes to the network; the mount load can share a de-duplicated result.
  const refresh = React.useCallback(() => load(true), [load]);

  React.useEffect(() => {
    if (authStatus === "authenticated") {
      load(false);
    } else if (authStatus === "anonymous") {
      setUsage(null);
      setStatus("idle");
    }
  }, [authStatus, load]);

  const value = React.useMemo(
    () => ({ usage, status, refresh }),
    [usage, status, refresh]
  );

  return (
    <UsageContext.Provider value={value}>{children}</UsageContext.Provider>
  );
}

export const useUsage = () => React.useContext(UsageContext);
